<script setup>
import { useQuality } from '@/composables/useQuality'
import { useAbility } from '@casl/vue'
import { useRoute, useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'Incident',
  },
})

const route = useRoute('quality-incidents-id')
const router = useRouter()
const ability = useAbility()
const { incident, loading, saving, fetchIncident, investigateIncident, closeIncident } = useQuality()

const load = () => fetchIncident(route.params.id)

onMounted(load)

const investigateDialog = ref(false)
const investigateForm = ref({ root_cause: '', analysis_method: '', pic_id: null, task_due_date: '' })
const investigateError = ref('')

const openInvestigate = () => {
  investigateForm.value = { root_cause: '', analysis_method: '', pic_id: null, task_due_date: '' }
  investigateError.value = ''
  investigateDialog.value = true
}

const saveInvestigation = async () => {
  investigateError.value = ''
  try {
    await investigateIncident(route.params.id, investigateForm.value)
    investigateDialog.value = false
    await load()
  } catch (e) {
    investigateError.value = e?.data?.message ?? 'Gagal menyimpan investigasi.'
  }
}

const closeError = ref('')

const doClose = async () => {
  closeError.value = ''
  try {
    await closeIncident(route.params.id)
    await load()
  } catch (e) {
    closeError.value = e?.data?.message ?? 'Insiden tidak bisa ditutup.'
  }
}

const statusColor = value => ({
  reported: 'warning',
  investigating: 'info',
  monitoring: 'primary',
  closed: 'success',
}[value] ?? 'secondary')
</script>

<template>
  <div>
    <div class="d-flex align-center gap-3 mb-4">
      <VBtn
        icon="ri-arrow-left-line"
        variant="text"
        @click="router.push({ name: 'quality-incidents' })"
      />
      <div class="flex-grow-1">
        <div class="d-flex align-center gap-3">
          <h4 class="text-h4 font-weight-bold mb-0">
            {{ incident?.incident_number }}
          </h4>
          <VChip
            v-if="incident"
            size="small"
            :color="statusColor(incident.status)"
            variant="tonal"
          >
            {{ incident.status }}
          </VChip>
        </div>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ incident?.title }}
        </p>
      </div>
    </div>

    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <template v-else-if="incident">
      <VRow>
        <VCol
          cols="12"
          md="8"
        >
          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0">
              Kronologi
            </VCardTitle>
            <VCardText class="pa-6">
              <p class="text-body-2 mb-2">
                <strong>Jenis:</strong> {{ incident.type?.name }} —
                <strong>Unit:</strong> {{ incident.unit?.name ?? '-' }} —
                <strong>Pelapor:</strong> {{ incident.reporter?.name ?? '-' }}
              </p>
              <p class="text-body-2 mb-0">
                {{ incident.chronology }}
              </p>
            </VCardText>
          </VCard>

          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0 d-flex align-center justify-space-between">
              Investigasi (RCA)
              <VBtn
                v-if="ability.can('investigate', 'Incident') && incident.status === 'reported'"
                size="small"
                prepend-icon="ri-search-eye-line"
                @click="openInvestigate"
              >
                Investigasi
              </VBtn>
            </VCardTitle>
            <VCardText class="pa-6">
              <VAlert
                v-if="!incident.investigations?.length"
                type="info"
                variant="tonal"
                density="compact"
              >
                Belum ada investigasi.
              </VAlert>
              <div
                v-for="inv in incident.investigations"
                :key="inv.id"
                class="mb-3"
              >
                <p class="text-body-2 font-weight-medium mb-1">
                  {{ inv.investigator ?? '-' }} — {{ inv.investigated_at }}
                </p>
                <p class="text-body-2 mb-0">
                  {{ inv.root_cause }}
                </p>
              </div>
            </VCardText>
          </VCard>

          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0 d-flex align-center justify-space-between">
              CAPA Terkait
              <VBtn
                v-if="ability.can('create', 'Capa')"
                size="small"
                prepend-icon="ri-add-line"
                @click="router.push({ name: 'quality-capa', query: { incident_id: incident.id } })"
              >
                Buat CAPA
              </VBtn>
            </VCardTitle>
            <VCardText class="pa-6">
              <VList v-if="incident.capas?.length">
                <VListItem
                  v-for="capa in incident.capas"
                  :key="capa.id"
                  :title="capa.title"
                  :subtitle="`Status: ${capa.status}`"
                  @click="router.push({ name: 'quality-capa-id', params: { id: capa.id } })"
                />
              </VList>
              <VAlert
                v-else
                type="info"
                variant="tonal"
                density="compact"
              >
                Belum ada CAPA untuk insiden ini.
              </VAlert>
            </VCardText>
          </VCard>

          <VCard>
            <VCardTitle class="pa-6 pb-0">
              Evidence
            </VCardTitle>
            <VCardText class="pa-6">
              <EvidenceUploader
                evidenceable-type="incident"
                :evidenceable-id="incident.id"
              />
            </VCardText>
          </VCard>
        </VCol>

        <VCol
          cols="12"
          md="4"
        >
          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0">
              Alur Penanganan
            </VCardTitle>
            <VCardText class="pa-6">
              <IncidentTimeline :incident="incident" />
            </VCardText>
          </VCard>

          <VCard v-if="ability.can('close', 'Incident') && incident.status !== 'closed'">
            <VCardText class="pa-6">
              <VAlert
                v-if="closeError"
                type="error"
                variant="tonal"
                density="compact"
                class="mb-4"
              >
                {{ closeError }}
              </VAlert>
              <VBtn
                block
                color="success"
                :loading="saving"
                @click="doClose"
              >
                Tutup Insiden
              </VBtn>
              <p class="text-caption text-medium-emphasis mt-2 mb-0">
                Hanya bisa ditutup setelah seluruh CAPA selesai (BR-009).
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>

    <VDialog
      v-model="investigateDialog"
      max-width="560"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          Investigasi (Root Cause Analysis)
        </VCardTitle>
        <VCardText class="pa-6">
          <VAlert
            v-if="investigateError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ investigateError }}
          </VAlert>
          <VRow>
            <VCol cols="12">
              <VTextarea
                v-model="investigateForm.root_cause"
                label="Akar Masalah (Root Cause) *"
                rows="3"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="investigateForm.analysis_method"
                label="Metode Analisis (opsional)"
                placeholder="mis. 5 Why, Fishbone"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="investigateForm.task_due_date"
                type="date"
                label="Tenggat Task RCA (opsional)"
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="investigateDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="saveInvestigation"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
